/**
 * Admin card to re-run Global-E platform migration for a selected portal with editable merchant id and environment.
 */
import { observer } from "mobx-react-lite";
import React from "react";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import { Button } from "../../../components/inputs/button/button";
import Card from "../../../components/layouts/card/card";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import ShopsSelector from "../../../components/shops-selector/shops-selector";
import { FormFieldLine } from "../../../components/form-field-line/form-field-line";
import { Input } from "../../../components/inputs/input/input";
import GlobaleEnvironmentSelector from "./components/globale-environment-selector/globale-environment-selector";
import reMigrateGlobalePlatformStore from "./stores/re-migrate-globale-platform-store";
import { isSet } from "../../../models/model-types-methods";
import { IShop } from "../../../models/User";
import { Note, NOTE_TYPE } from "../../../components/note/note";
import "./migrate-global-e-merchants.scss";

function formatMigrationFinishedAt(isoDate: string | null): string {
    if (!isoDate) {
        return "a previous migration run";
    }
    return new Date(isoDate).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

const ReMigrateGlobalePlatformStoreCard: React.FC = observer(() => {
    const store = reMigrateGlobalePlatformStore;
    const selected = store.portal;

    return (
        <Card
            isExpanded={true}
            toolbar={{
                title: (
                    <>
                        <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            Re-migrate Global-E Platform Store{" "}
                        </FontBody1>
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>(backend/global-e)</FontBody1>
                    </>
                ),
            }}
        >
            <ListLayout>
                <FormFieldLine label="Select portal to re-migrate">
                    <ShopsSelector
                        value={store.portal ? [store.portal] : ([] as IShop[])}
                        onChange={(shops: IShop[]) => store.setPortal(shops[0])}
                        disabled={store.isProcessing || isSet(selected)}
                        includeSecondaryPortals
                    />
                </FormFieldLine>

                {isSet(selected) && isSet(store.lastCompletedMigrationAttempt) ? (
                    <Note
                        type={NOTE_TYPE.WARNING}
                        caption="Note"
                        body={
                            <>
                                This store was migrated in {formatMigrationFinishedAt(store.lastCompletedMigrationAttempt.migration_finished_at)}.
                                Rerunning the migration will delete the store and create a new one.
                            </>
                        }
                    />
                ) : null}

                <FormFieldLine label="Global-E Merchant ID">
                    <Input value={store.merchantId} onChange={store.setMerchantId} required={true} />
                </FormFieldLine>

                <GlobaleEnvironmentSelector onChange={store.setEnvironment} value={store.environment} />

                <Button
                    onClick={() => void store.runMigration()}
                    text="Run Migration"
                    hasLoader={true}
                    isLoading={store.isProcessing}
                    disabled={store.isProcessing || !store.isValid}
                />

                {store.errorMessage ? (
                    <div className="results-message">
                        <FontBody1 color={FONT_COLOR.SEMANTIC_DANGER}>{store.errorMessage}</FontBody1>
                    </div>
                ) : null}
            </ListLayout>
        </Card>
    );
});

export default ReMigrateGlobalePlatformStoreCard;
